// WhatsApp in the desk (docs/specs/whatsapp.md): every conversation of the GHL
// WhatsApp number, also with numbers that are no lead, and writing back.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '../../auth/SessionProvider';
import {
  markWaRead,
  sendWa,
  useWaConversations,
  useWaMessages,
  type WaConversation,
} from '../../lib/data';
import { fDay, fShort, fTime } from '../../lib/format';
import { ErrorBox, useDrawer, useToast, useWho } from '../../ui/kit';

const initials = (name: string) =>
  name
    .replace(/[^\p{L}\p{N} ]/gu, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((x) => x[0]!.toUpperCase())
    .join('') || '#';

function windowLeft(c: WaConversation): string | null {
  if (!c.window_expires_at) return null;
  const ms = Date.parse(c.window_expires_at) - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3_600_000);
  return h >= 1 ? `${h} h` : `${Math.max(1, Math.round(ms / 60_000))} min`;
}

export function WhatsApp() {
  const list = useWaConversations();
  const [sel, setSel] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('chat'),
  );
  const [q, setQ] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list.data ?? []).filter(
      (c) =>
        (!unreadOnly || c.unread_count > 0) &&
        (!needle ||
          c.display_name.toLowerCase().includes(needle) ||
          c.phone_e164.includes(needle.replace(/\s/g, '')) ||
          (c.clinic_label ?? '').toLowerCase().includes(needle)),
    );
  }, [list.data, q, unreadOnly]);

  const current = (list.data ?? []).find((c) => c.id === sel) ?? null;
  const unread = (list.data ?? []).reduce((a, c) => a + c.unread_count, 0);

  return (
    <div className="ccx">
      <div className="head">
        <div>
          <h1>
            <span className="grad">WhatsApp</span>
          </h1>
          <p>
            Every conversation of the Ouradent number, also with people who are no lead yet.
            {unread ? ` ${unread} unread.` : ''}
          </p>
        </div>
      </div>
      <ErrorBox error={list.error} />
      <section className={`card wa${current ? ' open' : ''}`} data-testid="view-whatsapp">
        <div className="convs">
          <div className="wa-tools">
            <input
              type="search"
              aria-label="Search chats"
              placeholder="Search name, clinic or number…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button
              className="cc-chip"
              aria-pressed={unreadOnly}
              onClick={() => setUnreadOnly(!unreadOnly)}
            >
              Unread
            </button>
          </div>
          {list.isLoading && <div className="loading">Loading…</div>}
          {!list.isLoading && rows.length === 0 && (
            <div className="empty">
              {list.data?.length
                ? 'No chat matches.'
                : 'No WhatsApp chats yet. They appear here within a minute of the first message in GHL.'}
            </div>
          )}
          {rows.map((c) => (
            <button
              key={c.id}
              className="conv"
              aria-current={c.id === sel}
              onClick={() => {
                setSel(c.id);
                if (c.unread_count) void markWaRead(c.id);
              }}
            >
              <span className="av s wa-av">{initials(c.display_name)}</span>
              <div style={{ minWidth: 0 }}>
                <div className="n">{c.display_name}</div>
                <div className="p">{c.last_body ?? c.phone_e164}</div>
                <div className="p" style={{ fontSize: 12 }}>
                  {c.clinic_label ?? c.phone_e164}
                  {c.lead_id ? '' : ' · not a lead'}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: 'var(--soft)' }}>
                  {c.last_message_at ? fDay(c.last_message_at) : ''}
                </div>
                {c.unread_count > 0 && <span className="u">{c.unread_count}</span>}
              </div>
            </button>
          ))}
        </div>
        <div className="chat">
          {current ? (
            <Chat key={current.id} c={current} onBack={() => setSel(null)} />
          ) : (
            <div className="empty" style={{ margin: 'auto' }}>
              Pick a chat on the left.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Chat({ c, onBack }: { c: WaConversation; onBack: () => void }) {
  const { open } = useDrawer();
  const { can } = useSession();
  const toast = useToast();
  const who = useWho();
  const msgs = useWaMessages(c.id);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const left = windowLeft(c);

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [msgs.data?.length]);
  useEffect(() => {
    if (c.unread_count) void markWaRead(c.id);
  }, [c.id, c.unread_count, msgs.data?.length]);

  async function reopen() {
    setBusy(true);
    try {
      await sendWa(c.id, null, true);
      toast('Reminder template requested. It appears in the chat when GHL has sent it.');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await sendWa(c.id, t);
      setText('');
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="ch">
        <button className="btn sm wa-back" onClick={onBack}>
          Back
        </button>
        <span className="av s wa-av">{initials(c.display_name)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{c.display_name}</b>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {c.phone_e164}
            {c.clinic_label ? `, ${c.clinic_label}` : ''}
            {c.source ? `, ${c.source}` : ''}
          </div>
        </div>
        {c.lead_id && can('pipeline') && (
          <button className="btn sm" onClick={() => open({ type: 'lead', id: c.lead_id! })}>
            Open lead
          </button>
        )}
        <span className={`pill sm ${left ? 'te' : 'gr'}`}>
          {left ? `Open for ${left}` : 'Window closed'}
        </span>
      </div>
      <div className="msgs" id="msgs" data-testid="wa-messages">
        {msgs.isLoading && <div className="loading">Loading…</div>}
        {(msgs.data ?? []).map((m) => (
          <div key={m.id} className={`bub ${m.direction === 'in' ? 'in' : 'out'}`}>
            {m.body}
            <small>
              {m.sent_by ? `${who(m.sent_by).full_name}, ` : ''}
              {fShort(m.created_at)}
              {m.direction === 'out' ? `, ${m.status}` : ''}
            </small>
          </div>
        ))}
        <div ref={end} />
      </div>
      <div className="compose">
        {!left && (
          <div className="wa-closed">
            <p className="m" style={{ margin: 0 }}>
              WhatsApp lets free text through only for 24 hours after the number's last message. To
              write now, send the approved reminder template; the chat opens as soon as they answer.
            </p>
            <button className="btn sm primary" disabled={busy} onClick={() => void reopen()}>
              Send reminder template
            </button>
          </div>
        )}
        <div className="row2">
          <textarea
            aria-label="Message"
            rows={2}
            disabled={!left}
            placeholder={left ? 'Write a message' : 'Window closed'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
            }}
          />
          <button
            className="btn primary"
            disabled={!left || !text.trim() || busy}
            onClick={() => void send()}
          >
            Send
          </button>
        </div>
        <div className="m" style={{ fontSize: 12, marginTop: 6 }}>
          Last message{' '}
          {c.last_message_at ? `${fTime(c.last_message_at)}, ${fDay(c.last_message_at)}` : 'none'}.
          Ctrl or Cmd with Enter sends.
        </div>
      </div>
    </>
  );
}
